// Working hours and settings, checked the same way the database checks them (ORG-01, ORG-02) so the owner sees the
// problem before saving. The database stays the authority.
import { parseVisitSettings, type VisitSettings, visitSettingsJson, visitSettingsProblem } from "./visits";
import type { Json } from "./database.types";

export const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Day = (typeof DAYS)[number];
export type TimeWindow = [string, string];
export type WorkingHours = Partial<Record<Day, TimeWindow[]>>;

export const DAY_LABEL: Record<Day, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

const HHMM = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

export function isTime(s: unknown): s is string {
  return typeof s === "string" && HHMM.test(s);
}

export function parseWorkingHours(json: Json | null | undefined): WorkingHours {
  const out: WorkingHours = {};
  if (!json || typeof json !== "object" || Array.isArray(json)) return out;
  for (const day of DAYS) {
    const v = (json as Record<string, Json | undefined>)[day];
    if (Array.isArray(v)) {
      out[day] = v.filter((w): w is [string, string] => Array.isArray(w) && isTime(w[0]) && isTime(w[1]))
        .map((w) => [w[0], w[1]] as TimeWindow);
    }
  }
  return out;
}

export function workingHoursProblem(wh: WorkingHours): string | null {
  for (const day of DAYS) {
    const windows = wh[day] ?? [];
    if (windows.length > 4) return `${DAY_LABEL[day]}: at most four windows.`;
    for (const [start, end] of windows) {
      if (!isTime(start) || !isTime(end)) {
        return `${DAY_LABEL[day]}: use times like 10:00.`;
      }
      if (start >= end) {
        return `${DAY_LABEL[day]}: a window must start before it ends.`;
      }
    }
  }
  return null;
}

/**
 * When a call nobody answered is tried again (D-152): after the minutes set, or the next working day at the next part
 * of the day (a morning call in the afternoon, an afternoon call in the evening, an evening call in the morning).
 */
export const RETRY_MODES = ["minutes", "next_day"] as const;
export type RetryMode = (typeof RETRY_MODES)[number];

/** Follow-up settings the scheduler and rules read (defaults as in the database). */
export const FOLLOW_UP_DEFAULTS = {
  calling_from: "08:00",
  calling_until: "21:00",
  default_time: "11:00",
  morning: "11:00",
  afternoon: "15:00",
  evening: "18:00",
  vague_cutoff: "15:00",
  vague_delay_minutes: 120,
  retry_minutes: 120,
  retry_mode: "minutes" as RetryMode,
  max_attempts: 3,
  /** Keep retrying a customer who does not answer, with no limit (D-153); otherwise one last try a week after the limit. */
  retry_until_answered: false,
  quiet_after_days: 7,
  missed_call_rule: true,
};
export type FollowUp = typeof FOLLOW_UP_DEFAULTS;

export const NUMBER_RANGES: Record<
  "vague_delay_minutes" | "retry_minutes" | "max_attempts" | "quiet_after_days",
  [number, number]
> = {
  vague_delay_minutes: [5, 1440],
  retry_minutes: [15, 1440],
  max_attempts: [0, 10],
  quiet_after_days: [1, 90],
};

export const DIGEST_DEFAULTS = { time: "08:00", enabled: true };
export type DigestSettings = typeof DIGEST_DEFAULTS;

/** How long call audio is kept; transcripts stay (RET-01). */
export const RETENTION_DEFAULTS = { audio_days: 90 };
export type RetentionSettings = typeof RETENTION_DEFAULTS;

/**
 * How far back a phone reaches when it is set up: the last 3 or 7 days of calls and recordings (APP-09). And whether a
 * colleague's customer who calls a telecaller is named on her phone, with whose customer they are and their last
 * conversation, or only as a colleague's customer (D-094): named unless the company switches it off. And what her
 * phone does after a call she placed that nobody picked up (D-142): saves it as not connected and shows that for a
 * moment, asks her, or saves it without a word. And whether, with neither Coach nor Autopilot, the phones still send
 * call recordings, which no AI hears (D-170).
 */
export const UNANSWERED_CHOICES = ["save", "ask", "silent"] as const;
export type UnansweredChoice = (typeof UNANSWERED_CHOICES)[number];
export const PHONES_DEFAULTS: {
  history_days: 3 | 7;
  colleague_customers: boolean;
  unanswered: UnansweredChoice;
  sync_recordings: boolean;
} = {
  history_days: 3,
  colleague_customers: true,
  unanswered: "save",
  sync_recordings: false,
};
export type PhoneSettings = typeof PHONES_DEFAULTS;

export interface OrgSettings {
  follow_up: FollowUp;
  digest: DigestSettings;
  retention: RetentionSettings;
  phones: PhoneSettings;
  /** Who handles visits, where, the call to confirm one and the message on booking (D-186). */
  visits: VisitSettings;
}

export function parseSettings(json: Json | null | undefined): OrgSettings {
  const obj = json && typeof json === "object" && !Array.isArray(json)
    ? (json as Record<string, Json | undefined>)
    : {};
  const fu = (obj.follow_up && typeof obj.follow_up === "object" ? obj.follow_up : {}) as Record<string, unknown>;
  const dg = (obj.digest && typeof obj.digest === "object" ? obj.digest : {}) as Record<
    string,
    unknown
  >;
  const follow_up = { ...FOLLOW_UP_DEFAULTS };
  for (const key of Object.keys(FOLLOW_UP_DEFAULTS) as (keyof FollowUp)[]) {
    const v = fu[key];
    if (v !== undefined && typeof v === typeof FOLLOW_UP_DEFAULTS[key]) {
      (follow_up as Record<string, unknown>)[key] = v;
    }
  }
  if (!RETRY_MODES.includes(follow_up.retry_mode)) follow_up.retry_mode = FOLLOW_UP_DEFAULTS.retry_mode;
  return {
    follow_up,
    digest: {
      time: isTime(dg.time) ? dg.time : DIGEST_DEFAULTS.time,
      enabled: typeof dg.enabled === "boolean" ? dg.enabled : DIGEST_DEFAULTS.enabled,
    },
    retention: {
      audio_days: Number.isInteger((obj.retention as Record<string, unknown> | undefined)?.audio_days)
        ? (obj.retention as Record<string, number>).audio_days
        : RETENTION_DEFAULTS.audio_days,
    },
    phones: {
      history_days: (obj.phones as Record<string, unknown> | undefined)?.history_days === 7 ? 7 : 3,
      colleague_customers: (obj.phones as Record<string, unknown> | undefined)?.colleague_customers !== false,
      unanswered:
        UNANSWERED_CHOICES.find((c) => c === (obj.phones as Record<string, unknown> | undefined)?.unanswered) ??
          PHONES_DEFAULTS.unanswered,
      sync_recordings: (obj.phones as Record<string, unknown> | undefined)?.sync_recordings === true,
    },
    visits: parseVisitSettings(obj.visits),
  };
}

export function settingsProblem(s: OrgSettings): string | null {
  const fu = s.follow_up;
  for (
    const key of [
      "calling_from",
      "calling_until",
      "default_time",
      "morning",
      "afternoon",
      "evening",
      "vague_cutoff",
    ] as const
  ) {
    if (!isTime(fu[key])) {
      return `Use a time like 11:00 for ${key.replace(/_/g, " ")}.`;
    }
  }
  for (
    const [key, [lo, hi]] of Object.entries(NUMBER_RANGES) as [
      keyof typeof NUMBER_RANGES,
      [number, number],
    ][]
  ) {
    const v = fu[key];
    if (!Number.isInteger(v) || v < lo || v > hi) {
      return `${key.replace(/_/g, " ")} must be a whole number from ${lo} to ${hi}.`;
    }
  }
  if (fu.calling_from >= fu.calling_until) {
    return "Calling hours must start before they end.";
  }
  if (!RETRY_MODES.includes(fu.retry_mode)) {
    return "An unanswered call is tried again after the minutes set, or the next day.";
  }
  if (!isTime(s.digest.time)) return "Use a time like 08:00 for the digest.";
  if (!Number.isInteger(s.retention.audio_days) || s.retention.audio_days < 7 || s.retention.audio_days > 730) {
    return "Keep audio for 7 to 730 days.";
  }
  if (s.phones.history_days !== 3 && s.phones.history_days !== 7) {
    return "A new phone sends the last 3 or 7 days of calls and recordings.";
  }
  if (!UNANSWERED_CHOICES.includes(s.phones.unanswered)) {
    return "After an unanswered call the phone saves it, asks, or saves it silently.";
  }
  return visitSettingsProblem(s.visits);
}

/** The settings object to save: only known keys, so the database accepts it. Visit settings are kept whatever card is
 * saved, and left out when the company never set any, so its visits work as before (D-186). */
export function settingsJson(s: OrgSettings): Json {
  const visits = visitSettingsJson(s.visits);
  return {
    follow_up: { ...s.follow_up },
    digest: { ...s.digest },
    retention: { ...s.retention },
    phones: { ...s.phones },
    ...(visits ? { visits } : {}),
  };
}

export const TIMEZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Kathmandu",
  "Asia/Dhaka",
  "Europe/London",
  "America/New_York",
];
